import { LampDesk, Mic } from "@tamagui/lucide-icons";
import { useState } from "react";
import { ScrollView, YStack } from "tamagui";

import { useStartIdVerification } from "@/api/generated";
import { useGetUserProfile } from "@/api/queries/userProfile";
import videoCallImage from "@/assets/images/video-call2.png";
import womanVideoCallImage from "@/assets/images/woman-video-call.jpg";
import {
  BulletItem,
  Button,
  Footer,
  Header,
  Heading,
  Image,
  Paragraph,
} from "@/components";
import CuraiFeedback from "@/conversation/video/CuraiFeedback";
import { US_STATES } from "@/settings/constants";

const VIDEO_PREP_ITEMS = [
  {
    text: "Find a quiet, well-lit space where you can speak comfortably.",
    icon: <LampDesk size="$300" flexShrink={0} />,
  },
  {
    text: "You'll need to enable your camera and microphone to continue.",
    icon: <Mic size="$300" flexShrink={0} />,
  },
];

interface VideoVisitExplainerProps {
  onClose?: () => void;
  showAvRequiredForChatContent?: boolean;
  onContinue?: () => void;
}

const VideoVisitExplainer = ({
  onClose,
  showAvRequiredForChatContent = false,
  onContinue,
}: VideoVisitExplainerProps) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const { mutate: startIdVerification } = useStartIdVerification();
  const { data: userProfile, isLoading: isUserProfileLoading } =
    useGetUserProfile();

  const handleContinue = () => {
    if (!userProfile?.idVerification) {
      startIdVerification();
    }
    onContinue?.();
  };

  const isDataLoading = isUserProfileLoading;
  const stateLabel =
    US_STATES.find((state) => state.value === userProfile?.residingState)
      ?.label ?? "your state";
  const headingText = showAvRequiredForChatContent
    ? `Since you're in ${stateLabel}, your first visit has to be on video`
    : "Your first visit will be on video";
  const descriptionText = showAvRequiredForChatContent
    ? "After your first visit, you'll be able to choose between video and chat-based visits."
    : "When you come back, you can choose between video and chat-based visits.";
  const heroImage = showAvRequiredForChatContent
    ? womanVideoCallImage
    : videoCallImage;

  if (showFeedback) {
    return (
      <CuraiFeedback
        onClose={onClose ?? (() => {})}
        onReturnToVisit={() => setShowFeedback(false)}
      />
    );
  }

  const stickyFooter = (
    <Footer padding="$200" mode="dialog">
      <Button
        dataTrackingId="video-visit-explainer-continue-button"
        mode="primary"
        constrained
        onPress={handleContinue}
        disabled={isDataLoading}
      >
        Continue with video visit
      </Button>

      <Button
        dataTrackingId="video-visit-explainer-not-ready-link"
        mode="tertiary"
        onPress={() => {
          setShowFeedback(true);
        }}
      >
        I'm not ready yet
      </Button>
    </Footer>
  );

  return (
    <>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          flex: 1,
          gap: "$100",
          paddingHorizontal: "$200",
          paddingBottom: "$300",
        }}
      >
        <Header gap="$100">
          <Heading size="$xl">{headingText}</Heading>
          <Paragraph size="$sm" color="$textSecondary">
            {descriptionText}
          </Paragraph>
        </Header>

        <YStack
          marginTop="$200"
          marginBottom="auto"
          width="100%"
          $lg={{ maxWidth: 400, alignSelf: "center" }}
        >
          <Image
            source={heroImage}
            width="100%"
            height="auto"
            aspectRatio={16 / 9}
            objectFit="cover"
            borderTopLeftRadius="$150"
            borderTopRightRadius="$150"
            overflow="hidden"
          />

          <YStack
            backgroundColor="$fillCallout"
            padding="$300"
            gap="$200"
            borderBottomWidth={1}
            borderLeftWidth={1}
            borderRightWidth={1}
            borderColor="$strokeSubdued"
            borderBottomLeftRadius="$150"
            borderBottomRightRadius="$150"
            overflow="hidden"
          >
            {VIDEO_PREP_ITEMS.map((item) => (
              <BulletItem
                key={item.text}
                itemText={item.text}
                icon={item.icon}
                color="$textSecondary"
                size="$sm"
              />
            ))}
          </YStack>
        </YStack>
      </ScrollView>
      {stickyFooter}
    </>
  );
};

export default VideoVisitExplainer;
